import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { OnboardingState } from '../config/state.model';
import { AuthService } from './auth.service';
import { colVal } from './payload-builder';

@Injectable({ providedIn: 'root' })
export class ModuleOwnersApiService {
  private http = inject(HttpClient);
  private auth = inject(AuthService);

  private headers(token: string): HttpHeaders {
    return new HttpHeaders({ 'Content-Type': 'application/json', Authorization: `JWT ${token}` });
  }

  async submit(state: OnboardingState, baseUrl: string): Promise<void> {
    const { token } = await this.auth.ensureAuth();
    const hdrs = this.headers(token);

    const [modulesRes, usersRes] = await Promise.all([
      firstValueFrom(this.http.get<any[]>(`${baseUrl}/modules/`, { headers: hdrs })),
      firstValueFrom(this.http.get<any>(`${baseUrl}/users/filter-user/`, { headers: hdrs })),
    ]);

    // Build name → id lookup maps (case-insensitive)
    const moduleMap = new Map<string, number>();
    for (const m of modulesRes ?? []) {
      if (m.name)         moduleMap.set(m.name.toLowerCase(), m.id);
      if (m.display_name) moduleMap.set(m.display_name.toLowerCase(), m.id);
    }

    const userMap = new Map<string, number>();
    for (const u of usersRes?.users ?? usersRes ?? []) {
      const fullName = `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim().toLowerCase();
      if (fullName) userMap.set(fullName, u.id);
    }

    const unresolved: string[] = [];
    const payload = state.rows
      .map(row => {
        const zoneName  = colVal(state, 'zone_name',  row)?.toString().trim() ?? '';
        const ownerName = colVal(state, 'owner_name', row)?.toString().trim() ?? '';
        const module_id = moduleMap.get(zoneName.toLowerCase());
        const user_id   = userMap.get(ownerName.toLowerCase());
        if (module_id == null) unresolved.push(`Zone "${zoneName}" not found in Kepler`);
        if (user_id   == null) unresolved.push(`Owner "${ownerName}" not found in Kepler`);
        return { module_id, user_id };
      })
      .filter(r => r.module_id != null && r.user_id != null) as { module_id: number; user_id: number }[];

    if (!payload.length) {
      throw new Error('No rows could be resolved.\n' + unresolved.join('\n'));
    }
    if (unresolved.length) {
      console.warn('Some rows could not be resolved and were skipped:\n' + unresolved.join('\n'));
    }

    await firstValueFrom(
      this.http.post<any>(
        `${baseUrl}/users/access-inheritance/`,
        payload,
        { headers: hdrs }
      )
    );
  }
}
